import React from "react";
import { cn } from "@/lib/utils";
import { TrendingUp, TrendingDown, Minus, Shield, Zap } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

interface RiskProfile {
  id: string;
  label: string;
  description: string;
  volatility: number;
  expectedReturn: number;
  color: string;
  icon: React.ReactNode;
}

interface InvestmentProfileSelectorProps {
  title?: string;
  value?: string;
  onChange?: (profileId: string) => void;
  profiles?: RiskProfile[];
  animated?: boolean;
  className?: string;
}

const DEFAULT_PROFILES: RiskProfile[] = [
  {
    id: "sicherheit",
    label: "Sicherheit",
    description: "Kapitalerhalt, minimales Risiko",
    volatility: 5,
    expectedReturn: 1,
    color: "hsl(142 76% 36%)",
    icon: <Shield className="h-5 w-5" />,
  },
  {
    id: "konservativ",
    label: "Konservativ",
    description: "Geringe Schwankungen, stabiler Ertrag",
    volatility: 10,
    expectedReturn: 3,
    color: "hsl(173 80% 40%)",
    icon: <Minus className="h-5 w-5" />,
  },
  {
    id: "ausgewogen",
    label: "Ausgewogen",
    description: "Balance zwischen Risiko und Ertrag",
    volatility: 20,
    expectedReturn: 5,
    color: "hsl(var(--primary))",
    icon: <TrendingUp className="h-5 w-5" />,
  },
  {
    id: "wachstum",
    label: "Wachstum",
    description: "Höhere Schwankungen, höherer Ertrag",
    volatility: 30,
    expectedReturn: 7,
    color: "hsl(38 92% 50%)",
    icon: <TrendingUp className="h-5 w-5" />,
  },
  {
    id: "chancen",
    label: "Chancen",
    description: "Maximales Wachstum, hohes Risiko",
    volatility: 40,
    expectedReturn: 10,
    color: "hsl(0 84% 60%)",
    icon: <Zap className="h-5 w-5" />,
  },
];

// Generate sample chart data for each profile
const generateChartData = (volatility: number, expectedReturn: number) => {
  const data = [];
  let value = 100;

  for (let i = 0; i <= 24; i++) {
    const randomFactor = (Math.random() - 0.5) * volatility * 0.1;
    const trend = expectedReturn / 24;
    value = value * (1 + (trend + randomFactor) / 100);
    data.push({ month: i, value: Math.round(value) });
  }

  return data;
};

export function InvestmentProfileSelector({
  title = "Ihr Anlegerprofil",
  value,
  onChange,
  profiles = DEFAULT_PROFILES,
  animated = true,
  className,
}: InvestmentProfileSelectorProps) {
  const selectedProfile = profiles.find((p) => p.id === value) || profiles[2];

  return (
    <div className={cn("space-y-6", className)}>
      {title && <h3 className="font-semibold text-lg text-center">{title}</h3>}

      {/* Profile selector */}
      <div className="flex justify-center gap-1">
        {profiles.map((profile, idx) => (
          <button
            key={profile.id}
            onClick={() => onChange?.(profile.id)}
            className={cn(
              "flex flex-col items-center p-3 rounded-lg transition-all",
              "border-2 min-w-[80px]",
              animated && "animate-in fade-in slide-in-from-bottom-2",
              value === profile.id
                ? "border-primary bg-primary/10 scale-105"
                : "border-transparent hover:bg-muted/50"
            )}
            style={animated ? { animationDelay: `${idx * 50}ms` } : undefined}
          >
            <div style={{ color: profile.color }}>{profile.icon}</div>
            <span className="text-xs font-medium mt-1">{profile.label}</span>
          </button>
        ))}
      </div>

      {/* Selected profile chart */}
      <div
        className={cn(
          "p-4 rounded-lg border-2 transition-all",
          animated && "animate-in fade-in zoom-in-95"
        )}
        style={{ borderColor: selectedProfile.color }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="p-2 rounded-full"
            style={{ backgroundColor: `${selectedProfile.color}20`, color: selectedProfile.color }}
          >
            {selectedProfile.icon}
          </div>
          <div>
            <h4 className="font-semibold">{selectedProfile.label}</h4>
            <p className="text-sm text-muted-foreground">{selectedProfile.description}</p>
          </div>
        </div>

        {/* Chart */}
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={generateChartData(selectedProfile.volatility, selectedProfile.expectedReturn)}
              margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
            >
              <ReferenceLine y={100} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
              <XAxis dataKey="month" hide />
              <YAxis hide domain={["dataMin - 10", "dataMax + 10"]} />
              <Line
                type="monotone"
                dataKey="value"
                stroke={selectedProfile.color}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Erwartete Rendite</p>
            <p className="text-lg font-bold" style={{ color: selectedProfile.color }}>
              ca. {selectedProfile.expectedReturn}% p.a.
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Volatilität</p>
            <p className="text-lg font-bold" style={{ color: selectedProfile.color }}>
              ±{selectedProfile.volatility}%
            </p>
          </div>
        </div>
      </div>

      {/* Risk scale */}
      <div className="space-y-2">
        <div className="h-2 rounded-full bg-gradient-to-r from-emerald-500 via-amber-500 to-rose-500" />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Geringes Risiko</span>
          <span>Mittleres Risiko</span>
          <span>Hohes Risiko</span>
        </div>
      </div>
    </div>
  );
}
